import { useEffect, useState } from 'react';

/**
 * Reads the operating system request for reduced motion.
 *
 * Kept free of animation libraries so it can also gate non animated effects, and
 * tolerant of the older MediaQueryList listener API still shipped by some
 * WebViews and by the test environment.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const mediaQueryList = (): MediaQueryList | null => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }

  return window.matchMedia(REDUCED_MOTION_QUERY);
};

export const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(
    () => mediaQueryList()?.matches ?? false,
  );

  useEffect(() => {
    const list = mediaQueryList();

    if (!list) {
      return undefined;
    }

    const handleChange = (event: MediaQueryListEvent) => setPrefersReducedMotion(event.matches);

    setPrefersReducedMotion(list.matches);

    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', handleChange);

      return () => list.removeEventListener('change', handleChange);
    }

    if (typeof list.addListener === 'function') {
      list.addListener(handleChange);

      return () => list.removeListener(handleChange);
    }

    return undefined;
  }, []);

  return prefersReducedMotion;
};

export default usePrefersReducedMotion;
